import { useCallback, useEffect, useRef, useState } from "react"

export interface UseDialogConfirmCloseOptions {
    /**
     * External control of dialog open state (controlled mode).
     * When provided, the hook doesn't manage isDialogOpen internally.
     * The parent component is responsible for setting this value.
     */
    isOpen?: boolean
    /** Initial open state of the dialog (uncontrolled mode). Ignored if isOpen is provided. */
    defaultOpen?: boolean
    /** Callback when the dialog open state changes (after all confirmations) */
    onOpenChange?: (isOpen: boolean) => void
    /** Callback when the user confirms discarding changes */
    onDiscard?: () => void
    /**
     * Function that returns whether to show the confirmation prompt.
     * If omitted or returns true, the prompt is shown.
     * Use this to skip confirmation when there are no unsaved changes.
     *
     * @example
     * shouldConfirmClose: () => form.formState.isDirty
     */
    shouldConfirmClose?: () => boolean
}

export interface UseDialogConfirmCloseReturn {
    /** Whether the main dialog is open */
    isDialogOpen: boolean
    /** Whether the confirmation prompt is open */
    isPromptOpen: boolean
    /** Open the dialog */
    openDialog: () => void
    /** Close the dialog without showing confirmation (use for successful submissions) */
    closeDialog: () => void
    /** Attempt to close - shows confirmation prompt if shouldConfirmClose returns true */
    handleCloseAttempt: () => void
    /** Handler for Dialog.Root onOpenChange - intercepts close attempts */
    handleDialogOpenChange: (newOpen: boolean) => void
    /** Handler for when user cancels the discard prompt (dismisses the prompt) */
    handlePromptCancel: () => void
    /** Handler for when user confirms discarding changes */
    handlePromptDiscard: () => void
}

/**
 * Hook for managing a dialog that shows a confirmation prompt before closing.
 *
 * This hook handles the complex state management required when you want to:
 * 1. Intercept all close attempts (close button, cancel button, overlay click, escape key)
 * 2. Optionally show a confirmation prompt above the dialog asking if the user wants to discard changes
 * 3. Either dismiss the prompt (on cancel) or close everything (on confirm)
 *
 * The dialog stays open while the confirmation prompt appears above it.
 * The prompt's z-index ensures it renders on top of the dialog.
 *
 * @example
 * ```tsx
 * const {
 *     isDialogOpen,
 *     isPromptOpen,
 *     handleCloseAttempt,
 *     handleDialogOpenChange,
 *     handlePromptCancel,
 *     handlePromptDiscard,
 *     closeDialog,
 * } = useDialogConfirmClose({
 *     defaultOpen: false,
 *     onOpenChange: (isOpen) => console.log('Dialog is now', isOpen ? 'open' : 'closed'),
 *     onDiscard: () => console.log('Changes discarded'),
 *     shouldConfirmClose: () => form.formState.isDirty, // Only confirm if form has changes
 * })
 *
 * return (
 *     <>
 *         <Dialog.Root isOpen={isDialogOpen} onOpenChange={handleDialogOpenChange}>
 *             <Dialog.Content>
 *                 <DialogHeader.CloseButton onClick={handleCloseAttempt} />
 *                 <Button onClick={handleCloseAttempt}>Cancel</Button>
 *                 <Button onClick={closeDialog}>Submit</Button>
 *             </Dialog.Content>
 *         </Dialog.Root>
 *
 *         <Prompt
 *             isOpen={isPromptOpen}
 *             onCancel={handlePromptCancel}
 *             onConfirm={handlePromptDiscard}
 *         />
 *     </>
 * )
 * ```
 */
export default function useDialogConfirmClose(
    options: UseDialogConfirmCloseOptions = {},
): UseDialogConfirmCloseReturn {
    const { isOpen, defaultOpen = false, onOpenChange, onDiscard, shouldConfirmClose } = options

    const isControlled = isOpen !== undefined
    const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen)
    const [isPromptOpen, setIsPromptOpen] = useState(false)
    const isDialogOpen = isControlled ? isOpen : internalIsOpen

    // Refs for coordinating with Radix's onOpenChange callbacks.
    // Must be refs (not state) because they're checked synchronously before React re-renders.
    const isShowingPromptRef = useRef(false) // Confirmation prompt is visible
    const isDirectClosingRef = useRef(false) // Closing without confirmation (e.g., after success)
    const justOpenedRef = useRef(false) // Just opened in controlled mode (ignore spurious close)
    const prevIsOpenRef = useRef<boolean | undefined>(undefined) // Track isOpen changes

    // Detect when dialog opens in controlled mode. Runs during render (before Radix events)
    // so we can ignore spurious close events that Radix fires immediately after opening.
    if (isControlled && isOpen && !prevIsOpenRef.current) {
        justOpenedRef.current = true
    }
    prevIsOpenRef.current = isOpen

    const openDialog = useCallback(() => {
        isDirectClosingRef.current = false

        if (!isControlled) {
            setInternalIsOpen(true)
        }

        onOpenChange?.(true)
    }, [isControlled, onOpenChange])

    const closeDialog = useCallback(() => {
        setIsPromptOpen(false)
        isShowingPromptRef.current = false
        isDirectClosingRef.current = true

        if (!isControlled) {
            setInternalIsOpen(false)
        }

        onOpenChange?.(false)
    }, [isControlled, onOpenChange])

    const handleCloseAttempt = useCallback(() => {
        if (isShowingPromptRef.current) {
            return
        }

        if (shouldConfirmClose && !shouldConfirmClose()) {
            closeDialog()
            return
        }

        isShowingPromptRef.current = true
        setIsPromptOpen(true)
    }, [shouldConfirmClose, closeDialog])

    const handleDialogOpenChange = useCallback(
        (newOpen: boolean) => {
            if (isShowingPromptRef.current || isDirectClosingRef.current) {
                isDirectClosingRef.current = false
                return
            }

            if (newOpen) {
                justOpenedRef.current = false

                if (!isControlled) {
                    setInternalIsOpen(true)
                    onOpenChange?.(true)
                }
            } else if (isDialogOpen) {
                if (justOpenedRef.current) {
                    justOpenedRef.current = false
                    return
                }
                handleCloseAttempt()
            }
        },
        [isDialogOpen, handleCloseAttempt, onOpenChange, isControlled],
    )

    const handlePromptCancel = useCallback(() => {
        setIsPromptOpen(false)
        isShowingPromptRef.current = false
    }, [])

    const handlePromptDiscard = useCallback(() => {
        setIsPromptOpen(false)
        isShowingPromptRef.current = false
        isDirectClosingRef.current = true

        if (!isControlled) {
            setInternalIsOpen(false)
        }

        onDiscard?.()
        onOpenChange?.(false)
    }, [onDiscard, onOpenChange, isControlled])

    // Warn user before leaving the page if there are unsaved changes
    useEffect(() => {
        if (!isDialogOpen) {
            return
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            // Warn if shouldConfirmClose is not provided, or if it returns true
            if (!shouldConfirmClose || shouldConfirmClose()) {
                event.preventDefault()
            }
        }

        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => window.removeEventListener("beforeunload", handleBeforeUnload)
    }, [isDialogOpen, shouldConfirmClose])

    return {
        isDialogOpen,
        isPromptOpen,
        openDialog,
        closeDialog,
        handleCloseAttempt,
        handleDialogOpenChange,
        handlePromptCancel,
        handlePromptDiscard,
    }
}
